import faker from 'faker';
import {
  getExperimentDefinitions,
  type ExperimentOverride,
} from '../experiments/experiment-definitions';
import { type Environment, type Request } from '../types';

enum MealSnackCombos {
  NO_MEALS_OR_SNACKS = 0,
  SNACKS_ONLY = 1,
  MEALS_ONLY = 2,
  MEALS_AND_SNACKS = 3,
}

enum HouseholdSize {
  ONE_PERSON = 1,
  TWO_PEOPLE = 2,
  THREE_PEOPLE_OR_MORE = 3,
}

enum OccasionDaysPerWeek {
  NONE = 0,
  SOME = 1,
  ALL = 2,
}

enum SweetsAnswer {
  NO_THANKS = 0,
  SO_SO = 1,
  ABSOLUTELY = 2,
}

enum FruitAnswer {
  NO_THANKS = 0,
  SO_SO = 1,
  ABSOLUTELY = 2,
}

interface Quiz {
  household_size?: HouseholdSize;
  num_adults?: number;
  num_young_children?: number;
  num_older_children?: number;
  weekly_grocery_spend?: number;
  breakfast_meal_snack?: MealSnackCombos;
  lunch_meal_snack?: MealSnackCombos;
  dinner_meal_snack?: MealSnackCombos;
  breakfast_frequency?: OccasionDaysPerWeek;
  lunch_frequency?: OccasionDaysPerWeek;
  dinner_frequency?: OccasionDaysPerWeek;
  snack_frequency?: OccasionDaysPerWeek;
  sweets_frequency?: SweetsAnswer;
  fruit_frequency?: FruitAnswer;
}

interface PricingConfig {
  version: number;
  breakfastPacks: number;
  numLunch: number;
  numDinner: number;
  snackPacks: number;
  sweetsPacks: number;
  fruitPacks: number;
  servingSize: number;
  loading: boolean;
  shipping: number;
  allow4Servings: boolean;
  firstBoxTotal: number;
  subtotal: number;
  promo_effect: {
    promos: Array<{
      code: string;
      type: number;
      value: number;
      is_expired: boolean;
      effect: string;
    }>;
    free_item_effect: string;
    discount_effect: string;
    effect_str: string;
    effect_base: string;
  };
}

type Customer = Required<{
  id?: number;
  carrier?: number;
  credit?: string;
  did_checkout?: boolean;
  is_on_hold?: boolean;
  is_on_hold_next_week?: boolean;
  is_vip?: boolean;
  on_hold_until_date?: string | null;
  origin?: number;
  plan?: string | null;
  plan_subtotal?: string;
  referral_code?: string | null;
  rollover_credits?: string;
  rollover_credit_expiration?: string | null;
  timezone?: string | null;
  quiz?: string;
  day_of_week?: number;
  email: string;
  first_name: string;
  last_name: string;
  life_promo?: string | null;
  frequency?: number;
  spicy_level: number;
}>;

interface CustomerAddress {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
  company: string;
  note: string;
  is_commercial: boolean;
  sms_opt_in: boolean;
}

export enum DietaryRestrictions {
  Omnivore = 11,
  Vegan = 1,
  Vegetarian = 2,
  Pescatarian = 3,
  DairyFree = 4,
  GlutenFree = 5,
  SoyFree = 6,
  TreeNutFree = 7,
  PeanutFree = 8,
  EggFree = 9,
  ShellfishFree = 10,
}

export interface CustomerProfile {
  quiz?: Quiz;
  dietary?: DietaryRestrictions[];
  address: CustomerAddress;
  pricingConfig?: PricingConfig;
  paymentInfo?: {
    stripe_token: string;
  };
}

interface SignupParams {
  email: string;
  firstName: string;
  lastName: string;
}

interface Params {
  customer: SignupParams;
  profile: CustomerProfile;
  experiments: ExperimentOverride[];
  useTruncatedOverrideExperimentIds?: true;
}

interface CreateTestCustomerBody {
  // Basic customer information
  email?: string;
  first_name?: string;
  last_name?: string;

  // Experiments
  experiments?: {
    experiment_id: number;
    variant: number;
  }[];

  // Dietary restrictions
  dietary_restrictions?: {
    id: number;
    selected: boolean;
  }[];

  // Quiz information
  household_size?: HouseholdSize;
  num_adults?: number;
  num_young_children?: number;
  num_older_children?: number;
  weekly_grocery_spend?: number;
  breakfast_frequency?: OccasionDaysPerWeek;
  lunch_frequency?: OccasionDaysPerWeek;
  dinner_frequency?: OccasionDaysPerWeek;
  snack_frequency?: OccasionDaysPerWeek;
  sweets_frequency?: SweetsAnswer;
  fruit_frequency?: FruitAnswer;
  breakfast_meal_snack?: MealSnackCombos;
  lunch_meal_snack?: MealSnackCombos;
  dinner_meal_snack?: MealSnackCombos;

  // Address information
  address1: string;
  city: string;
  state: string;
  zip_code?: string;
}

export type CustomerSignupEndpoints =
  | '/api/test/create_test_customer/'
  | '/test/get_user_cart/'
  | '/api/save_payment/'
  | '/api/v2/customers/self/'
  | '/api/v2/auth/login/'
  | '/api/enroll_experiments/'
  | '/api/v2/experiments/'
  | '/api/place_checkout_order/'
  | '/';

export const DEFAULT_USER_PASSWORD = 'hungryrootveggiesarethebest';

export function generateCustomerCredentials() {
  // faker has trouble when run in parallel, adding more randomness
  const randomNumber = Math.floor(Math.random() * 1000) + 1;
  const randomString = randomNumber.toString();
  const firstName = faker.name.firstName() + randomString;
  const lastName = faker.name.lastName();
  return {
    email: faker.internet
      .email(
        `web-user-${firstName}`,
        `${lastName}-${randomNumber}`,
        'hungryroot.com'
      )
      .toLowerCase(),
    password: DEFAULT_USER_PASSWORD,
    firstName: firstName,
    lastName: lastName,
  };
}

export async function customerSignUp(params: Params, environment: Environment) {
  const headers = {
    AUTHORIZATION: 'c61de580-e48d-41d2-a8d1-6cd1429987a1',
  };
  const {
    customer: customerData,
    profile,
    experiments: experimentOverrides,
    useTruncatedOverrideExperimentIds,
  } = params;
  const { network, log = () => void 0 } = environment;

  async function networkWithLogging<T>(
    requestConfig: Request<unknown>,
    status?: string
  ) {
    const { path, method } = requestConfig;
    if (status !== undefined) {
      log(status);
    }
    try {
      const response = await network<T>(requestConfig);
      log(`[${method}] ${path} - Response Status: ${response.status}`);
      return {
        response,
        request: requestConfig,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      log(`[${method}] ${path} - ERROR: ${errorMessage}`);
      throw error;
    }
  }

  const { preSignUp, postSignUp } = await getExperimentDefinitions(
    {
      experimentOverrides,
      options: {
        useTruncatedOverrideExperimentIds,
      },
    },
    environment
  );

  const search: Request['query'] = {};
  if (preSignUp.length > 0) {
    preSignUp.forEach(({ experiment, variant }) => {
      search[experiment.shortName] = variant.toString();
    });
    const req: Request<unknown> = {
      path: '/',
      method: 'GET',
      query: search,
      headers,
    };

    await networkWithLogging(req);
  }

  const { email, firstName, lastName } = customerData;
  const { dietary, quiz, address, paymentInfo } = profile;

  const testCustomerBody: CreateTestCustomerBody = {
    // Basic customer information
    email,
    first_name: firstName,
    last_name: lastName,

    // Experiments
    experiments: postSignUp.map(({ experiment, variant }) => ({
      experiment_id: experiment.id,
      variant,
    })),

    // Dietary restrictions
    dietary_restrictions: dietary?.map((value) => ({
      id: value,
      selected: true,
    })),

    // Quiz information
    household_size: quiz?.household_size,
    num_adults: quiz?.num_adults,
    num_young_children: quiz?.num_young_children,
    num_older_children: quiz?.num_older_children,
    weekly_grocery_spend: quiz?.weekly_grocery_spend,
    breakfast_frequency: quiz?.breakfast_frequency,
    lunch_frequency: quiz?.lunch_frequency,
    dinner_frequency: quiz?.dinner_frequency,
    snack_frequency: quiz?.snack_frequency,
    sweets_frequency: quiz?.sweets_frequency,
    fruit_frequency: quiz?.fruit_frequency,
    breakfast_meal_snack: quiz?.breakfast_meal_snack,
    lunch_meal_snack: quiz?.lunch_meal_snack,
    dinner_meal_snack: quiz?.dinner_meal_snack,

    // Address information
    address1: address.address1,
    city: address.city,
    state: address.state,
    zip_code: address?.zip_code,
  };

  const createTestCustomerResponse = await networkWithLogging(
    {
      path: '/api/test/create_test_customer/',
      body: testCustomerBody,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...headers,
      },
      method: 'POST',
    },
    `Creating test customer with data: ${JSON.stringify(
      testCustomerBody,
      null,
      2
    )}`
  );

  if (createTestCustomerResponse.response.status !== 201) {
    throw new Error('Unable to create test customer');
  }

  const loginResponse = await networkWithLogging(
    {
      path: '/api/v2/auth/login/',
      method: 'POST',
      headers,
      body: {
        email,
        password: DEFAULT_USER_PASSWORD,
        captcha_token: 'string', // Can not be empty but accepts any string in lower envs
      },
    },
    'Logging new user in after creation'
  );

  if (loginResponse.response.status !== 202) {
    throw new Error('Unable to log in with newly created test customer');
  }

  const userCartResp = await networkWithLogging<{ id: number }>(
    {
      path: '/test/get_user_cart/',
      method: 'GET',
      headers,
    },
    'Getting user cart'
  );

  if (userCartResp.response.status !== 200) {
    throw new Error(
      `Could not retrieve cart: ${JSON.stringify(userCartResp, null, 2)}`
    );
  }

  if (address === undefined) {
    return;
  }

  const { id: cartId } = userCartResp.response.data;

  if (paymentInfo === undefined) {
    return;
  }

  await networkWithLogging(
    {
      path: '/api/save_payment/',
      method: 'POST',
      headers,
      body: {
        stripe_token: paymentInfo.stripe_token,
        order_id: cartId,
        method_name: 'basic-card',
      },
    },
    'Saving Payment Info'
  );

  // Add retry logic for customers/self endpoint
  const maxRetries = 3;
  let retryCount = 0;

  // Initialize selfResponse with a default error response
  let selfResponse = {
    status: 403,
    data: {} as Customer,
  };

  while (retryCount < maxRetries) {
    selfResponse = (
      await networkWithLogging<Customer>({
        path: `/api/v2/customers/self/`,
        method: 'GET',
        headers,
        query: {
          // Add timestamp to avoid caching issues
          _t: Date.now().toString(),
        },
      })
    ).response;

    if (selfResponse.status === 200) {
      break;
    }

    log(
      `Attempt ${
        retryCount + 1
      }/${maxRetries}: Unable to get customer. Status: ${
        selfResponse.status
      }. Retrying in 2 second...`
    );
    await new Promise((resolve) => setTimeout(resolve, 2000));
    retryCount++;
  }

  if (selfResponse.status !== 200) {
    // Try to login again and retry customer/self
    log(`Attempting to login again before giving up...`);

    const loginResponse = (
      await networkWithLogging({
        path: '/api/v2/auth/login/',
        method: 'POST',
        headers,
        body: {
          email,
          password: DEFAULT_USER_PASSWORD,
          captcha_token: 'string', // Can not be empty but accepts any string in lower envs
        },
      })
    ).response;

    if (loginResponse.status === 202) {
      log(`Login successful, retrying customer/self request`);
      // Retry the self request after successful login
      selfResponse = (
        await networkWithLogging<Customer>({
          path: `/api/v2/customers/self/`,
          method: 'GET',
          headers,
          query: {
            _t: Date.now().toString(),
          },
        })
      ).response;

      if (selfResponse.status === 200) {
        return selfResponse.data;
      }
    }

    throw new Error(
      `Unable to get customer! ${JSON.stringify(selfResponse, null, 2)}`
    );
  }

  return selfResponse.data;
}
